import { useMemo } from 'react';
import { useResultsDataContext } from '../../../context/ResultsDataContext';
import { useFilters } from '../../../context/filter-context/FilterContext';
import { ScorableItemTableDataGroup } from '../../per-student/hooks/useTableData';
import { useTranslation } from 'react-i18next';
import { MaterialIcon } from '../../../../components/MaterialIcon';
import { ScorableItem } from '../../../api/scorable-items';
import useResolvedTableOfContent, { ResolvedTableOfContentNode } from '../../../hooks/useResolvedTableOfContent';

function getFlatNodeIdsFromNodeSubtree(node: ResolvedTableOfContentNode, flatNodeIds: string[] = []): string[] {
  flatNodeIds.push(node.id);
  if (!node.nodes || node.nodes.length === 0) return flatNodeIds;

  for (const child of node.nodes) {
    getFlatNodeIdsFromNodeSubtree(child, flatNodeIds);
  }
  return flatNodeIds;
}

function getScorableItemsForNodes(nodeIds: string[], scorableItems: ScorableItem[], isVem?: boolean) {
  return scorableItems.filter(item =>
    isVem ? item.exerciseCategories?.some(cat => nodeIds.includes(cat)) : nodeIds.includes(item.node),
  );
}

export function useScorableItemTOCHierarchy() {
  const { scorableItemsPerSchoolYearPerModule } = useResultsDataContext();
  const { selectedSchoolYear, selectedModule } = useFilters();
  const { toc } = useResolvedTableOfContent();

  const { t } = useTranslation();

  const scorableItemsOfSchoolYearAndModule = useMemo(
    () => scorableItemsPerSchoolYearPerModule[selectedSchoolYear?.id || '']?.[selectedModule?.id || ''] || [],
    [scorableItemsPerSchoolYearPerModule, selectedModule?.id, selectedSchoolYear?.id],
  );

  const tocAndScorableItems = useMemo(() => {
    if (!selectedModule || !toc) return [];

    const mapNodeTree = (
      node: ResolvedTableOfContentNode,
      level = 0,
      parentNode?: ResolvedTableOfContentNode,
    ): ScorableItemTableDataGroup | null => {
      const nodeHierarchy = getFlatNodeIdsFromNodeSubtree(node);
      if (!nodeHierarchy) return null;

      const isVem = selectedModule.isVirtualExerciseModule;
      const scorableItemsInNodeSubtree = getScorableItemsForNodes(
        nodeHierarchy,
        scorableItemsOfSchoolYearAndModule,
        isVem,
      );

      if (scorableItemsInNodeSubtree.length === 0) return null;

      const scorableItemsOfNode = scorableItemsOfSchoolYearAndModule.filter(item =>
        isVem ? item.exerciseCategories?.some(cat => cat === node.id) : item.node === node.id,
      );

      return {
        id: node.id,
        accumulatedRow: {
          id: node.id,
          title: node.title,
          tableGroupId: level === 0 || !parentNode ? undefined : parentNode.id,
          image: node.image,
          level: level,
          subTitleTop: node.prefix,
          columns: [],
        },
        rows: scorableItemsOfNode.map(item => {
          const scorableItemProps = item.isAssignment
            ? ({
                type: 'assignment',
                assignmentRanges: item.assignmentRanges,
              } as const)
            : ({ type: 'free-exercise' } as const);

          return {
            id: item.id,
            title: item.name,
            tableGroupId: node.id,
            level: level + 1,
            ...scorableItemProps,
            icon: (
              <MaterialIcon materialType={item.type} contentType={item.contentType} className="scorable-item__image" />
            ),
            subTitleBot: t(
              `PAGES.RESULTS.PER_STUDENT.TABLE.SCORABLE_ITEM.TYPE.${
                item.isAssignment ? 'ASSIGNMENT' : 'FREE-EXERCISE'
              }`,
            ),

            columns: [],
          };
        }),
        groups: node.nodes?.length
          ? (node.nodes
              .map(childNode => mapNodeTree(childNode, level + 1, node))
              .filter(Boolean) as ScorableItemTableDataGroup[])
          : [],
      };
    };

    return toc.map(node => mapNodeTree(node)).filter(Boolean) as ScorableItemTableDataGroup[];
  }, [scorableItemsOfSchoolYearAndModule, selectedModule, t, toc]);

  return {
    tocAndScorableItems,
  };
}
